import { BaseApiClient } from './base-client'
import {
  API_ROUTES,
  interpolateRoute,
  type ApiResponse,
} from '@super/shared-types'

export interface PartnerInfo {
  id: string
  name: string
  integrationType: string
  status: string
}

export interface PartnerCredentials {
  clientId: string | null
  clientSecretHint: string | null
  generatedAt: string | null
}

export interface GenerateCredentialsResponse {
  clientId: string
  clientSecret: string
  message: string
}

export interface ProvisionedCompany {
  id: string
  name: string
  slug: string
  status: string
  integrationStatus: string
  provisionedAt: string
  lastSyncStatus?: string | null
  lastSyncCompletedAt?: string | null
  lastSyncError?: string | null
}

export interface CompaniesResponse {
  companies: ProvisionedCompany[]
  pageInfo: {
    hasNextPage: boolean
    endCursor: string | null
  }
  total: number
}

export class PartnerClient extends BaseApiClient {
  async getCredentials(): Promise<PartnerCredentials> {
    const response = await this.request<ApiResponse<PartnerCredentials>>(
      API_ROUTES.PARTNER.CREDENTIALS,
      {
        method: 'GET',
      },
    )
    return response.data!
  }

  async generateCredentials(): Promise<GenerateCredentialsResponse> {
    const response = await this.request<
      ApiResponse<GenerateCredentialsResponse>
    >(API_ROUTES.PARTNER.CREDENTIALS, {
      method: 'POST',
    })
    return response.data!
  }

  async getProvisionedCompanies(
    limit?: number,
    after?: string,
  ): Promise<CompaniesResponse> {
    const params = new URLSearchParams()
    if (limit) params.append('limit', limit.toString())
    if (after) params.append('after', after)

    const url = `${API_ROUTES.PARTNER.COMPANIES}${params.toString() ? `?${params.toString()}` : ''}`
    const response = await this.request<ApiResponse<CompaniesResponse>>(url, {
      method: 'GET',
    })
    return response.data!
  }

  // Admin methods (for super-data-admins viewing partner companies)

  async getPartnerById(partnerId: string): Promise<PartnerInfo> {
    const url = interpolateRoute(API_ROUTES.ADMIN.PARTNERS.GET, {
      id: partnerId,
    })
    const response = await this.request<ApiResponse<PartnerInfo>>(url, {
      method: 'GET',
    })
    return response.data!
  }

  async getCredentialsAsAdmin(partnerId: string): Promise<PartnerCredentials> {
    const url = interpolateRoute(API_ROUTES.ADMIN.PARTNERS.GET_CREDENTIALS, {
      id: partnerId,
    })
    const response = await this.request<ApiResponse<PartnerCredentials>>(url, {
      method: 'GET',
    })
    return response.data!
  }

  async getProvisionedCompaniesAsAdmin(
    partnerId: string,
    limit?: number,
    after?: string,
  ): Promise<CompaniesResponse> {
    const params = new URLSearchParams()
    if (limit) params.append('limit', limit.toString())
    if (after) params.append('after', after)

    const url = `${interpolateRoute(API_ROUTES.ADMIN.PARTNERS.GET_COMPANIES, { id: partnerId })}${params.toString() ? `?${params.toString()}` : ''}`
    const response = await this.request<ApiResponse<CompaniesResponse>>(url, {
      method: 'GET',
    })
    return response.data!
  }
}

export const partnerClient = new PartnerClient()
